<template>
    <ContentComp class="vh-50">
        <div class="d-flex gap-2">
            <ToolbarComp
                :disabled="disableSearch"
                v-model:searchtext="searchText"
                search-box-placeholder="general.search"
                @searching="onSearchClick"
                class="flex-fill"
            >
                <template #first>
                    <DropdownComp
                        translationkey="participantsearch.searchSettings"
                        :dropdownItems="dropdownItems"
                        auto-close="outside"
                    />
                </template>
                <template #last>
                    <ButtonComp
                        button-type="Search"
                        :loadstate="searchstate"
                        @click="() => onSearchClick(searchText)"
                        :disabled="disableSearch"
                        >{{ $tm('general.search') }}</ButtonComp
                    >
                </template>
            </ToolbarComp>
        </div>

        <AlertMessage
            v-if="event?.FederationID == WellKnown.FEDERATION_NSSF"
            type="info"
            size="sm"
            ><span v-html="$tm('participantsearch.willOnlyIncludeWithMinIdrett', '', true)"></span
        ></AlertMessage>

        <AlertMessage v-if="!isSearchTextValid"><span v-html="$tm('search.min5characters', '', true)"></span></AlertMessage>

        <GridComp
            ref="grid"
            :class="[fullRowSelection === true ? 'ag-full-row-selection' : '']"
            :read-only="true"
            :grid-setup="gridSetup"
            resize-strategy="fitToContent"
            @on-selection-changed="onSelectionChanged"
            @on-cell-focused="onCellFocused"
            @on-cell-key-down="onCellKeyDown"
            @on-cell-double-clicked="onCellDoubleClicked"
        />
    </ContentComp>
</template>

<script setup lang="ts">
import { GridColumnDefinition, GridDataSource, GridSetup } from '@/components/Grid/Classes';
import GridColumnOptions, { GridColumnOptionsBuilder } from '@/components/Grid/Classes/GridColumnOptions';
import type { IGridRowGetterParams } from '@/components/Grid/Classes/GridDataSource';
import { GridComp, ContentComp, ButtonComp, AlertMessage, ToolbarComp, DisplayCountry, DisplayText, DropdownComp } from '@/components';
import { ParticipantSearchRequest, type Participant } from '@/objects/generated';
import { computed, onMounted, ref } from 'vue';
import type { ComponentExposed } from 'vue-component-type-helpers';
import { TaskLoaderState, useLoaders, useTaskLoader } from '@/composeables';
import { ParticipantDao } from '@/dao';
import { arrayUnique, propertyof } from '@/utilities';
import { CapitalizeWords } from '@/utilities/utils';
import ParticipantSignupStatusButton from './ParticipantSignupStatusButton.vue';
import ParticipantCategoryDisplay from './ParticipantCategoryDisplay.vue';
import { DropdownItemCheckbox, DropdownItemSeparator } from '@/objects';
import { useEventCommonStoreRef, useToastStore } from '@/stores';
import WellKnown from '@/objects/generated/static/WellKnown';
import { formatDateDefault } from '@/utilities/dateutils';
import { useDebouncer } from '@/utilities/Debouncer';
import { watch } from 'vue';

/**
 * Will present a participant search field and a grid with the results.
 *
 * NOTE! This will search for all participants in the database, not just the ones that are in the current event. and it also need to
 * take into account gdpr and user roles.
 *
 * update the modelValue with a selected Participant object
 */

const props = withDefaults(
    defineProps<{
        /**
         * The selected participant
         */
        modelValue: Participant | null;

        /**
         * This is to show the signup button in the grid
         */
        quickSignupEnabled?: boolean;

        /**
         * The state of the loader
         */
        loadState?: TaskLoaderState;

        /**
         * If we want to show a checkbox selection in the grid
         */
        checkboxSelection?: boolean;

        /**
         * We can provide an event so we will get some extra data when we search for participants.
         */
        eventId?: number;

        /**
         * The loader to save the state
         */
        loadStateLoader?: (loader: () => Promise<void>, key: string, icon: string | null, singleloader?: boolean) => Promise<void>;

        /**
         * We will select a row with the arrows and press enter to select
         * @default false
         */
        fullRowSelection?: boolean;

        /**
         * If set, it will start by searching for this text
         */
        prefillSearchText?: string;

        /**
         * Show more detailed colums, i.e club, nif, fis, last changed, last event
         */
        details?: boolean;
    }>(),
    {
        details: true,
    },
);

const emit = defineEmits<{
    (e: 'update:modelValue', val: Participant | null): void;

    // Emit when the signup button is clicked
    (e: 'quicksignup', participant: Participant): void;

    // Search completed
    (e: 'searchcompleted', participants: Participant[]): void;

    // When the enter key is pressed in full row selection mode
    (e: 'participantSelected', participant: Participant): void;
}>();

const grid = ref<ComponentExposed<typeof GridComp<Participant>> | null>(null);

/**
 * The list of participants in the grid
 */
const participants = ref<Participant[]>([]);

/**
 * The current search text
 */
const searchText = ref<string>('');

const { AddLoader, LoadState: searchstate } = useTaskLoader();

// Load the organizers from the common store
useLoaders(AddLoader, ['ORGANIZERS']);

const minimumSearchTextLength = 3;

// If the minimum search text is valid
const isSearchTextValid = ref<boolean>(true);

const event = useEventCommonStoreRef().Event;

// parameters with default values
const onlyWithNCFLicense = ref(false);
const onlyWithFISCode = ref(false);
const onlyUsedByMyOrganizer = ref(false);
const showMergedParticipants = ref(false);

// settings for search
const dropdownItems = computed(() => {
    const settings = [];

    settings.push(
        DropdownItemCheckbox.create({
            modelValue: showMergedParticipants,
            label: 'participantsearch.includeMergedParticipants',
        }),
    );

    settings.push(DropdownItemSeparator.create());

    settings.push(
        DropdownItemCheckbox.create({
            modelValue: onlyUsedByMyOrganizer,
            label: 'participantsearch.onlyUsedByMyOrganizer',
        }),
    );
    if (event.value?.FederationID == WellKnown.FEDERATION_NCF) {
        settings.push(
            DropdownItemCheckbox.create({
                modelValue: onlyWithNCFLicense,
                label: 'participantsearch.onlyWithNCFLicense',
            }),
        );
    }
    if (event.value?.FederationID == WellKnown.FEDERATION_SKIFORBUNDET) {
        settings.push(
            DropdownItemCheckbox.create({
                modelValue: onlyWithFISCode,
                label: 'participantsearch.onlyWithFISCode',
            }),
        );
    }

    return settings;
});

// Set up the columns for the participant grid
let columns: GridColumnOptions<Participant>[] = [];
let col = new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('Firstname'), 'racecontestantcomplete.firstname')
    .CellRenderer(DisplayText)
    .SortDisabled()
    .ReadOnly()
    .Build();

col.filterValues = () => {
    let uniqueValues = arrayUnique(participants.value.map((p) => CapitalizeWords(p.Firstname)));
    return new Map<string, any>(uniqueValues.map((v) => [v, v]));
};
columns.push(col);

let col2 = new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('Surname'), 'racecontestantcomplete.surname')
    .CellRenderer(DisplayText)
    .ReadOnly()
    .Build();
col2.filterValues = () => {
    let uniqueValues = arrayUnique(participants.value.map((p) => CapitalizeWords(p.Surname)));
    return new Map<string, any>(uniqueValues.map((v) => [v, v]));
};
columns.push(col2);

new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('Birthday'), 'racecontestantcomplete.birthdate', undefined, 'date')
    .Size(130, 130)
    .ReadOnly()
    .InsertAtEnd(columns);

let col4 = new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('City'), 'racecontestantcomplete.city')
    .CellRenderer(DisplayText)
    .Filterable()
    .Build();
col4.filterValues = () => {
    let uniqueValues = arrayUnique(participants.value.map((p) => CapitalizeWords(p.City)));
    return new Map<string, any>(uniqueValues.map((v) => [v, v]));
};
columns.push(col4);

let col5 = new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('NationalityCountryID'), 'general.country')
    .CellRenderer(DisplayCountry)
    .Filterable()
    .Size(180, 180)
    .ReadOnly()
    .Build();
col5.filterValues = () => {
    let uniqueValues = arrayUnique(participants.value.map((p) => p.NationalityCountryID));
    return new Map<string, any>(uniqueValues.map((v) => [v.toString(), v]));
};

columns.push(col5);

if (props.details === true) {
    let colClub = new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('LastEventClubName'), 'racecontestantcomplete.club')
        .CellRenderer(DisplayText)
        .Build();
    columns.push(colClub);

    let colLastChanged = new GridColumnOptionsBuilder<Participant>(
        propertyof<Participant>('LastChanged'),
        'general.lastChanged',
        undefined,
        'datetime',
    )
        .CellRenderer(DisplayText)
        .ValueGetter((params) => {
            return formatDateDefault(params.LastChanged, 'DATE');
        })
        .Size(130, 130)
        .ReadOnly()
        .Build();

    columns.push(colLastChanged);

    let colLastEvent = new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('LastEvent'), 'participant.lastEvent')
        .CellRenderer(DisplayText)
        .ValueGetter((params) => {
            if ((params.LastEvent ?? '') == '') {
                return '';
            }
            return `${params.LastEvent} ( ${params.LastEventDate ? formatDateDefault(params.LastEventDate, 'DATE') : ''})`;
        })
        .ReadOnly()
        .Build();
    columns.push(colLastEvent);

    let nifCodeCol = new GridColumnOptionsBuilder<Participant>(
        propertyof<Participant>('ParticipantIDs'),
        'import.nifid',
        propertyof<Participant>('ParticipantIDs') + '_nif',
    )
        .CellRenderer(DisplayText)
        .ReadOnly()
        .ValueGetter((params) => {
            const nif = params.ParticipantIDs?.find((id) => id.ParticipantIDTypeID === WellKnown.IDTYPE_NIF);
            return nif ? nif.Value : '';
        })
        .Build();
    columns.push(nifCodeCol);

    let fisCodeCol = new GridColumnOptionsBuilder<Participant>(
        propertyof<Participant>('ParticipantIDs'),
        'fis.fis',
        propertyof<Participant>('ParticipantIDs') + '_fis',
    )
        .CellRenderer(DisplayText)
        .ReadOnly()
        .ValueGetter((params) => {
            const fis = params.ParticipantIDs?.find((id) => id.ParticipantIDTypeID === WellKnown.IDTYPE_FIS_CODE);
            return fis ? fis.Value : '';
        })
        .Build();
    columns.push(fisCodeCol);

    let uciCodeCol = new GridColumnOptionsBuilder<Participant>(
        propertyof<Participant>('ParticipantIDs'),
        'import.uci',
        propertyof<Participant>('ParticipantIDs') + '_uci',
    )
        .CellRenderer(DisplayText)
        .ReadOnly()
        .ValueGetter((params) => {
            const uci = params.ParticipantIDs?.find((id) => id.ParticipantIDTypeID === WellKnown.IDTYPE_UCI_ID);
            return uci ? uci.Value : '';
        })
        .Build();
    columns.push(uciCodeCol);

    // new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('NIFCode'), 'participant.nif').ReadOnly().BuildAndInsertAt(columns, 2);
    // new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('FISCode'), 'participant.fis').ReadOnly().BuildAndInsertAt(columns, 3);
    // new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('LastChanged'), 'general.lastChanged', undefined, 'datetime')
    //     .Size(200, 200)
    //     .ReadOnly()
    //     .BuildAndInsertAt(columns, 4);
    // new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('LastEventName'), 'participant.lastEvent').ReadOnly().BuildAndInsertAt(columns, 5);
}

/**
 * We add a column for quick register button
 */
if (props.quickSignupEnabled === true) {
    let colCategory = new GridColumnOptionsBuilder<Participant>(propertyof<Participant>('ID'), 'general.category', 'category')
        .CellRenderer(ParticipantCategoryDisplay)
        .ReadOnly()
        .Build();
    columns.push(colCategory);

    let colRegister = new GridColumnOptionsBuilder<Participant>(
        propertyof<Participant>('ID'),
        'racecontestantcomplete.register',
        'register',
    )
        .CellRenderer(ParticipantSignupStatusButton, {
            onClick: (row: Participant) => {
                emit('quicksignup', row);
            },
        })
        .Size(180, 180)
        .SortDisabled()
        .ReadOnly()
        .Build();
    columns.unshift(colRegister);
}

/**
 * Handling click on the search button
 * @param event
 */
const onSearchClick = (searchString: string) => {
    searchParticipants(searchString ?? '');
};

const disableSearch = ref<boolean>(false);

/**
 * When a participant is selected or deselected
 * @param selected
 */
const onSelectionChanged = (selected: Participant[]) => {
    selectedParticipant.value = selected.at(0) ?? null;
    emit('update:modelValue', selected.length > 0 ? selected[0] : null);
};

/**
 * Search for participants
 * @param searchText
 */
const searchParticipants = (searchText: string) => {
    isSearchTextValid.value = searchText.trim().length >= minimumSearchTextLength;

    if (!isSearchTextValid.value) {
        return;
    }

    disableSearch.value = true;

    // If we have a loadStateLoader, we use that to load the state
    if (props.loadStateLoader != null) {
        props.loadStateLoader(() => searchMethod(searchText), 'searchparticipants', 'fa-search');
        return;
    }
    AddLoader(async () => await searchMethod(searchText), '');
};

/**
 * The method that does the actual search for participants and handles the response
 * @param searchText
 */
const searchMethod = async (searchText: string) => {
    const search = ParticipantSearchRequest.FromJsValues({
        Query: searchText,
        EventID: props.eventId ?? null,
        SearchType: 'Contains',
        Maxitems: 99999,
        OnlyWithFISCode: onlyWithFISCode.value,
        OnlyUsedByMyOrganizer: onlyUsedByMyOrganizer.value,
        OnlyWithNCFLicense: onlyWithNCFLicense.value,
        IncludeMerged: showMergedParticipants.value,
    });

    try {
        participants.value = await ParticipantDao.ParticipantSearch(search);

        disableSearch.value = false;

        grid.value?.refreshCachedData();

        grid.value?.focus('first');

        emit('searchcompleted', participants.value);
    } catch (e: any) {
        useToastStore().addError(e.message);
    }
};

/**
 * The setup of the grid
 */
const gridSetup = new GridSetup<Participant>({
    rowIdGetter: (row: Participant) => row.ID.toString(),
    datasource: new GridDataSource({
        rowGetter: (p: IGridRowGetterParams<Participant>) => {
            p.sort(participants.value);
            const filtered = p.filter(participants.value);

            const slice = filtered.slice(p.startRow, p.endRow);
            return p.successCallback(slice, filtered, filtered.length);
        },
    }),
    hasCheckboxSelectionColumn: props.checkboxSelection ?? false,
    rowSelection: 'single',
    GridColumnDefinition: new GridColumnDefinition({ ColumnOptions: columns }),
});

const selectedParticipant = ref<Participant | null>(null);

/**
 * When a cell is focused in the grid
 * @param rowid
 * @param columnId
 */
const onCellFocused = (rowid: string, columnId: string) => {
    if (props.fullRowSelection !== true) {
        return;
    }
    // We get the data row and emit the selected participant
    // The focus event can be called before the data is rendered in the grid and then it doesnt know tha data in the row so then we pick the first row since we know the order
    selectedParticipant.value =
        (rowid == undefined ? participants.value.at(0) : participants.value.find((p) => p.ID.toString() === rowid)) ?? null;

    if (selectedParticipant.value) {
        onSelectionChanged([selectedParticipant.value]);
    }
};

/**
 * Whenever a key is pressed in the grid
 * We handle only the enter key to select the participant
 * @param event
 */
const onCellKeyDown = (event: KeyboardEvent) => {
    if (props.fullRowSelection !== true) {
        return;
    }
    if (event.key === 'Enter' && selectedParticipant.value) {
        onSelectionChanged([selectedParticipant.value]);
        emit('participantSelected', selectedParticipant.value);
    }
};

/**
 * When a cell is double clicked
 * @param participant
 * @param celldata
 * @param rowid
 * @param columnId
 */
const onCellDoubleClicked = (participant: Participant, celldata: any, rowid: string, columnId: string) => {
    if (props.fullRowSelection !== true) {
        return;
    }
    onSelectionChanged([participant]);
    emit('participantSelected', participant);
};

const filterDebouncer = useDebouncer();

/**
 * If the filters are changed we auto refresh the grid to make sure the filter values are correct
 */
watch([onlyWithNCFLicense, onlyWithFISCode, onlyUsedByMyOrganizer, showMergedParticipants], () => {
    filterDebouncer(() => {
        onSearchClick(searchText.value);
    }, 1000);
});

onMounted(() => {
    if (props.prefillSearchText) {
        searchText.value = props.prefillSearchText;
        onSearchClick(props.prefillSearchText);
    }
});

defineExpose({
    /**
     * Refresh the grid data
     */
    updateRows: (rows: Participant[]) => {
        // We need to update the participants list and refresh the grid
        const rowIds: string[] = [];

        rows.forEach((p) => {
            const index = participants.value.findIndex((r) => r.ID === p.ID);
            if (index >= 0) {
                participants.value[index] = p;
                rowIds.push(p.ID.toString());
            }
        });
        // grid.value?.purgeCachedData();
        grid.value?.refreshCells(rowIds, ['ID', 'category', 'register']);
    },

    /**
     * We expose the selectedParticipant so its available if we are using this in a dialog
     */
    selectedParticipant,
});
</script>
